import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Spinner, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { getCreditBalanceCreditsBalanceGetOptions } from '@/client/@tanstack/react-query.gen';
import { GlobalOverlayLoader } from '@/components/common/GlobalOverlayLoader';
import { Routes } from '@/routes.ts';

const PAYMENT_JUST_CONFIRMED_KEY = 'paymentJustConfirmed';
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 10;
const REDIRECT_DELAY_MS = 1500;

/**
 * Handles the post-payment redirect after checkout.
 * When a payment was just confirmed (sessionStorage flag set by checkout flow or Stripe URL params),
 * polls the account balance until it reflects the purchase before redirecting.
 * This avoids the webhook delivery race condition where balance is still 0
 * immediately after a successful payment.
 */
function PaymentSuccessPage() {
	const navigate = useNavigate();

	const params = new URLSearchParams(window.location.search);
	const hasStripeParams =
		(!!params.get('payment_intent') && params.get('status') === 'succeeded') || !!params.get('session_id');

	if (hasStripeParams && sessionStorage.getItem(PAYMENT_JUST_CONFIRMED_KEY) !== 'true') {
		sessionStorage.setItem(PAYMENT_JUST_CONFIRMED_KEY, 'true');
	}

	const paymentJustConfirmed = sessionStorage.getItem(PAYMENT_JUST_CONFIRMED_KEY) === 'true';

	const { data: creditBalance } = useQuery({
		...getCreditBalanceCreditsBalanceGetOptions(),
		staleTime: 0,
		gcTime: 0,
		refetchInterval: (query) => {
			if (!paymentJustConfirmed) return false;
			const credits = query.state.data?.available_credits ?? 0;
			if (credits > 0) return false;
			const fetchCount = query.state.fetchFailureCount + (query.state.dataUpdateCount ?? 0);
			if (fetchCount >= MAX_POLL_ATTEMPTS) return false;
			return POLL_INTERVAL_MS;
		},
	});

	const availableCredits = creditBalance?.available_credits ?? 0;
	const creditsConfirmed = !paymentJustConfirmed || availableCredits > 0;

	useEffect(() => {
		if (!creditsConfirmed) return;
		sessionStorage.removeItem(PAYMENT_JUST_CONFIRMED_KEY);
		const timer = setTimeout(
			() =>
				navigate({
					to: Routes.HOUSE_SETUP_NEW,
				}),
			REDIRECT_DELAY_MS,
		);
		return () => clearTimeout(timer);
	}, [creditsConfirmed, navigate]);

	const isPolling = paymentJustConfirmed && availableCredits === 0;

	return (
		<GlobalOverlayLoader text="Payment successful">
			<VStack gap={4} textAlign="center">
				<Text fontSize="sm" color="fg.muted">
					{isPolling
						? 'Confirming your purchase, please wait...'
						: 'Your payment was confirmed. Redirecting to property setup...'}
				</Text>
				<Spinner size="lg" color="brand.fg" />
			</VStack>
		</GlobalOverlayLoader>
	);
}

export const Route = createFileRoute({
	component: PaymentSuccessPage,
});
